'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { BaseButton } from '@7f-ui/components/button/button';
import { MerchantCTA } from '@feat/feat-search/merchant-cta';
import type * as SearchFieldModuleType from '@feat/feat-search/search-field';
import { trackClickCta } from '@feat/feat-tracking/cta';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { startTransition, useEffect, useEffectEvent, useRef, useState } from 'react';
import type { HeaderConfig } from './header.types';
type SearchFieldModule = typeof SearchFieldModuleType;
type SearchFieldComponent = SearchFieldModule['SearchField'];
type HeaderSearchFieldProps = {
  config: HeaderConfig;
};
export function HeaderSearchField({
  config
}: HeaderSearchFieldProps) {
  const [SearchField, setSearchField] = useState<SearchFieldComponent | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const searchFieldModuleRef = useRef<SearchFieldModule | null>(null);
  const loadPromiseRef = useRef<Promise<SearchFieldModule> | null>(null);
  const openOnLoadRef = useRef(false);
  const ensureSearchFieldLoaded = useEffectEvent(async () => {
    if (searchFieldModuleRef.current) {
      return searchFieldModuleRef.current;
    }
    if (!loadPromiseRef.current) {
      loadPromiseRef.current = import('@feat/feat-search/search-field').then(module => {
        searchFieldModuleRef.current = module;
        startTransition(() => {
          setSearchField(() => module.SearchField);
        });
        return module;
      }).catch(error => {
        loadPromiseRef.current = null;
        console.error('Header search field load failed', error);
        throw error;
      });
    }
    return loadPromiseRef.current;
  });
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      void ensureSearchFieldLoaded();
    }, 0);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);
  useEffect(() => {
    if (!SearchField || !openOnLoadRef.current) {
      return;
    }
    openOnLoadRef.current = false;
    setIsActivating(false);
    requestAnimationFrame(() => {
      searchFieldModuleRef.current?.openSearchField();
    });
  }, [SearchField]);
  if (SearchField) {
    return <SearchField config={config} />;
  }
  return <HeaderSearchFieldShell config={config} isActivating={isActivating} onActivate={() => {
    openOnLoadRef.current = true;
    setIsActivating(true);
    void ensureSearchFieldLoaded().catch(() => {
      openOnLoadRef.current = false;
      setIsActivating(false);
    });
  }} onPreload={() => {
    void ensureSearchFieldLoaded();
  }} />;
}
type HeaderSearchFieldShellProps = {
  config: HeaderConfig;
  isActivating: boolean;
  onActivate: () => void;
  onPreload: () => void;
};
function HeaderSearchFieldShell(t0) {
  const $ = _c(27);
  const {
    config,
    isActivating,
    onActivate,
    onPreload
  } = t0;
  let t1;
  if ($[0] !== isActivating) {
    t1 = cx("hover:layer-bg-hover-dark active:layer-bg-active-dark relative mr-auto hidden h-10 w-48 items-center gap-2 rounded-xl p-3 text-white focus:outline-hidden focus:ring-0 sm:flex", {
      "cursor-progress opacity-90": isActivating
    });
    $[0] = isActivating;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      backgroundColor: "#425F55"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onActivate) {
    t3 = () => {
      trackClickCta("open search", {
        type: "search"
      });
      onActivate();
    };
    $[3] = onActivate;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <MagnifyingGlass className="shrink-0" size={20} weight="bold" />;
    t5 = <span className="font-body-small">Reiseziel</span>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== isActivating || $[8] !== onPreload || $[9] !== t1 || $[10] !== t3) {
    t6 = <BaseButton type="button" className={t1} style={t2} aria-busy={isActivating} onClick={t3} onFocus={onPreload} onMouseEnter={onPreload}>{t4}{t5}</BaseButton>;
    $[7] = isActivating;
    $[8] = onPreload;
    $[9] = t1;
    $[10] = t3;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== isActivating) {
    t7 = cx("hover:layer-bg-hover-dark active:layer-bg-active-dark mr-auto flex h-10 w-full max-w-48 items-center gap-2 rounded-xl bg-primary-green p-3 sm:hidden sm:h-12", {
      "cursor-progress opacity-90": isActivating
    });
    $[12] = isActivating;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <MagnifyingGlass className="shrink-0" size={20} weight="bold" />;
    t9 = <span className="font-body-small">Reiseziel</span>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  let t10;
  if ($[16] !== isActivating || $[17] !== onActivate || $[18] !== onPreload || $[19] !== t7) {
    t10 = <BaseButton type="button" className={t7} aria-busy={isActivating} onClick={onActivate} onFocus={onPreload}>{t8}{t9}</BaseButton>;
    $[16] = isActivating;
    $[17] = onActivate;
    $[18] = onPreload;
    $[19] = t7;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== config) {
    t11 = <div className="absolute inset-y-0 right-0 hidden items-center md:flex"><MerchantCTA config={config} /></div>;
    $[21] = config;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t6) {
    t12 = <div className="relative flex flex-1 justify-between">{t6}{t10}{t11}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t6;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}